<template>
  <div class="min-h-screen flex flex-col bg-tn-bg text-tn-ink">
    <header class="sticky top-0 z-50 bg-tn-bg/85 backdrop-blur-md border-b border-white/10">
      <div class="max-w-1700 mx-auto px-6 lg:px-8 h-16 lg:h-20 flex items-center gap-4 lg:gap-8">

        <RouterLink to="/" class="flex items-center h-10 lg:h-14 shrink-0" aria-label="Home">
          <TripleNetworkLogo />
        </RouterLink>

        <!-- Menus from D1 (falls back to hardcoded if API not ready) -->
        <nav class="hidden lg:flex items-center gap-1 xl:gap-2 text-sm font-medium">
          <template v-for="m in menus" :key="m.id">
            <div v-if="m.children.length" class="relative group" @mouseenter="openMenu = m.id" @mouseleave="openMenu = null">
              <RouterLink :to="m.url" class="nav-link flex items-center gap-1.5"
                          :class="{ 'nav-link--active': isActive(m) }">
                <i v-if="m.icon" :class="'fas ' + m.icon" class="text-xs opacity-70"></i>
                {{ m.name }}
                <i class="fas fa-chevron-down text-[10px] opacity-50 ml-0.5"></i>
              </RouterLink>
              <div v-show="openMenu === m.id" class="absolute left-0 top-full pt-2 w-72">
                <div class="bg-tn-bg-2 border border-white/10 rounded-lg shadow-2xl overflow-hidden">
                  <RouterLink v-for="ch in m.children" :key="ch.id" :to="ch.url" class="dropdown-item">
                    <i v-if="ch.icon" :class="'fas ' + ch.icon" class="text-tn-accent w-5"></i>
                    <span v-else class="w-5"></span>
                    {{ ch.name }}
                  </RouterLink>
                </div>
              </div>
            </div>
            <RouterLink v-else :to="m.url" class="nav-link flex items-center gap-1.5"
                        :class="{ 'nav-link--active': isActive(m) }">
              <i v-if="m.icon" :class="'fas ' + m.icon" class="text-xs opacity-70"></i>
              {{ m.name }}
            </RouterLink>
          </template>
        </nav>

        <div class="ml-auto flex items-center gap-2 lg:gap-3 text-sm">
          <a v-if="phone" :href="`tel:${phone.replace(/[^+0-9]/g,'')}`"
             class="hidden xl:inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/15 hover:border-white/30 hover:bg-white/5 transition whitespace-nowrap text-white font-semibold">
            <span class="text-tn-blue-400">{{ phone }}</span>
          </a>

          <template v-if="user">
            <RouterLink :to="isAdmin ? '/admin' : '/account'"
              class="hidden md:inline-flex items-center gap-1 px-3 py-2 rounded-md border border-white/15 hover:border-white/30 hover:bg-white/5 transition text-white text-sm">
              <span class="text-tn-accent">●</span> {{ accountLabel }}
            </RouterLink>
          </template>
          <template v-else>
            <RouterLink to="/login"
              class="hidden md:inline-flex items-center px-3 py-2 rounded-md border border-white/15 hover:border-white/30 hover:bg-white/5 transition text-white text-sm">
              Sign in
            </RouterLink>
          </template>

          <RouterLink to="/contact" class="btn-primary">Get a Quote</RouterLink>
        </div>
      </div>
    </header>

    <main class="flex-1">
      <slot />
    </main>

    <!-- Footer built from D1 footer_sections -->
    <footer class="border-t border-white/10 bg-tn-bg-2/60 mt-16">
      <div class="max-w-1700 mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 text-sm">
        <!-- company_info section -->
        <template v-for="s in footerSections" :key="s.id">
          <div v-if="s.section_type === 'company_info'" class="col-span-2">
            <div class="h-10 mb-4"><TripleNetworkLogo variant="wordless" /></div>
            <p class="text-tn-ink-dim leading-relaxed max-w-md mb-4">{{ s.content?.about }}</p>
            <p v-if="cms.footer_about" class="text-tn-ink-dim text-xs">{{ cms.footer_about }}</p>
          </div>
          <div v-else-if="s.section_type === 'link_column'">
            <h3 v-if="s.title" class="text-tn-ink font-semibold mb-3">{{ s.title }}</h3>
            <ul class="space-y-2 text-tn-ink-dim">
              <li v-for="(l, i) in (s.content?.links ?? [])" :key="i">
                <template v-if="l.url && l.url.startsWith('/')">
                  <RouterLink :to="l.url" :class="l.bold ? 'text-tn-accent font-semibold hover:underline' : 'hover:text-tn-accent'">{{ l.label }}</RouterLink>
                </template>
                <template v-else>
                  <a :href="l.url || '#'" :class="l.bold ? 'text-tn-accent font-semibold hover:underline' : 'hover:text-tn-accent'">{{ l.label }}</a>
                </template>
              </li>
            </ul>
          </div>
          <div v-else-if="s.section_type === 'contact_newsletter'">
            <h3 class="text-tn-ink font-semibold mb-3">{{ s.title || 'Get in Touch' }}</h3>
            <ul class="space-y-2 text-tn-ink-dim">
              <li v-if="s.content?.phone"><a :href="`tel:${s.content.phone.replace(/[^+0-9]/g,'')}`" class="hover:text-tn-accent">{{ s.content.phone }}</a></li>
              <li v-if="s.content?.email"><a :href="`mailto:${s.content.email}`" class="hover:text-tn-accent">{{ s.content.email }}</a></li>
              <li v-if="s.content?.address">{{ s.content.address }}</li>
            </ul>
          </div>
        </template>
      </div>

      <div class="border-t border-white/10">
        <div class="max-w-1700 mx-auto px-6 lg:px-8 py-4 text-xs text-tn-ink-dim flex flex-wrap justify-between gap-2">
          <span>© {{ new Date().getFullYear() }} {{ cms.site_name || 'Triple Network' }}. All rights reserved.</span>
          <span>Powered by Cloudflare</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import TripleNetworkLogo from '@/components/Brand/TripleNetworkLogo.vue';
import { useAuth } from '@/lib/auth';
import { menus, footerSections, cmsSettings as cms, loadCms } from '@/lib/cms';
import { useRoute } from 'vue-router';

const { user, isAdmin } = useAuth();
const route = useRoute();
const openMenu = ref<number | null>(null);

const accountLabel = computed(() => {
  if (!user.value) return '';
  return isAdmin.value ? 'Admin' : (user.value.name || user.value.email.split('@')[0]);
});
const phone = computed(() => cms.value.contact_phone || '+1 949-335-6010');

function isActive(m: any) {
  if (m.url === '/') return route.name === 'home';
  return route.path === m.url || route.path.startsWith(m.url + '/');
}

onMounted(() => { loadCms(); });
</script>

<style scoped>
.nav-link {
  @apply px-3 py-2 rounded-md text-tn-ink/80 hover:text-white hover:bg-white/5 transition;
}
.nav-link--active {
  @apply text-tn-accent bg-white/5;
}
.dropdown-item {
  @apply flex items-center gap-3 px-4 py-2.5 text-sm text-tn-ink/80 hover:text-white hover:bg-white/5 transition;
}
</style>
